/**
 * Carrier va vakansiya o'ng tugma menyulari: holat va rolga qarab (test: menu.test.ts).
 * O'zgartirish va arxivlash -- faqat menejer (backend: ManagerWritesOthersRead).
 */
import { Archive, Briefcase, CircleDot, Copy, ExternalLink, Eye, Link2, Mail, Pencil, Phone, Plus, Truck, UserPlus, UserRound } from "lucide-react";
import { STATUS_BG } from "@/components/ui/badge";
import { compactMenu, SEPARATOR, type MenuEntry } from "@/components/ui/context-menu";
import type { Translate } from "@/features/drivers/menu";
import type { Carrier, CarrierStatus, Job, JobStatus, Truck as TruckRow, TruckStatus } from "./types";

const CARRIER_STATUSES: CarrierStatus[] = ["active", "paused", "stopped"];
const JOB_STATUSES: JobStatus[] = ["open", "filled", "closed"];

export interface CarrierMenuActions {
  open: () => void;
  openNewTab: () => void;
  navigate: (path: string) => void;
  copy: (text: string) => void;
  call: (phone: string) => void;
  email: (address: string) => void;
  newJob: () => void;
  edit: () => void;
  setStatus: (status: CarrierStatus) => void;
  archive: () => void;
}

/** canEdit -- "carriers_edit" ruxsati (qo'shish, o'zgartirish, arxivlash); ko'rish hammaga */
export function carrierMenu(c: Carrier, canEdit: boolean, a: CarrierMenuActions, t: Translate, tc: Translate): MenuEntry[] {
  return compactMenu([
    { key: "open", label: t("open"), icon: Eye, onSelect: a.open },
    { key: "open-tab", label: t("openNewTab"), icon: ExternalLink, onSelect: a.openNewTab },
    SEPARATOR,
    !!c.contact_phone && { key: "call", label: t("call"), icon: Phone, hint: c.contact_phone, onSelect: () => a.call(c.contact_phone) },
    !!c.contact_phone && { key: "copy-phone", label: t("copyPhone"), icon: Copy, onSelect: () => a.copy(c.contact_phone) },
    !!c.contact_email && { key: "email", label: t("sendEmail"), icon: Mail, hint: c.contact_email, onSelect: () => a.email(c.contact_email) },
    !!c.contact_email && { key: "copy-email", label: t("copyEmail"), icon: Copy, onSelect: () => a.copy(c.contact_email) },
    !!c.mc_number && { key: "copy-mc", label: t("carrier.copyMc", { mc: c.mc_number }), icon: Copy, onSelect: () => a.copy(c.mc_number) },
    SEPARATOR,
    { key: "jobs", label: t("carrier.showJobs", { count: c.open_jobs }), icon: Briefcase, onSelect: () => a.navigate(`/jobs?carrier=${c.id}`) },
    canEdit && c.status !== "stopped" && { key: "new-job", label: t("carrier.newJob"), icon: Plus, onSelect: a.newJob },
    SEPARATOR,
    canEdit && {
      key: "status",
      label: t("setStatus"),
      icon: CircleDot,
      hint: tc(`status.${c.status}`),
      items: CARRIER_STATUSES.map((s) => ({ key: `status-${s}`, label: tc(`status.${s}`), dot: STATUS_BG[s], checked: s === c.status, disabled: s === c.status, onSelect: () => a.setStatus(s) })),
    },
    canEdit && { key: "edit", label: t("edit"), icon: Pencil, onSelect: a.edit },
    SEPARATOR,
    canEdit && { key: "archive", label: t("archive"), icon: Archive, danger: true, onSelect: a.archive },
  ]);
}

export interface JobMenuActions {
  open: () => void;
  openNewTab: () => void;
  copyLink: () => void;
  navigate: (path: string) => void;
  submitDriver: () => void;
  edit: () => void;
  setStatus: (status: JobStatus) => void;
  archive: () => void;
}

export function jobMenu(j: Job, canEdit: boolean, a: JobMenuActions, t: Translate, tc: Translate): MenuEntry[] {
  return compactMenu([
    { key: "open", label: t("open"), icon: Eye, onSelect: a.open },
    { key: "open-tab", label: t("openNewTab"), icon: ExternalLink, onSelect: a.openNewTab },
    { key: "copy-link", label: t("copyLink"), icon: Link2, onSelect: a.copyLink },
    SEPARATOR,
    j.status === "open" && j.carrier_status !== "stopped" && { key: "submit", label: t("job.submitDriver"), icon: UserPlus, onSelect: a.submitDriver },
    { key: "carrier", label: t("job.openCarrier", { name: j.carrier_name }), icon: Truck, onSelect: () => a.navigate(`/carriers/${j.carrier}`) },
    SEPARATOR,
    canEdit && {
      key: "status",
      label: t("setStatus"),
      icon: CircleDot,
      hint: tc(`status.${j.status}`),
      items: JOB_STATUSES.map((s) => ({ key: `status-${s}`, label: tc(`status.${s}`), dot: STATUS_BG[s], checked: s === j.status, disabled: s === j.status, onSelect: () => a.setStatus(s) })),
    },
    canEdit && { key: "edit", label: t("edit"), icon: Pencil, onSelect: a.edit },
    SEPARATOR,
    canEdit && { key: "archive", label: t("archive"), icon: Archive, danger: true, onSelect: a.archive },
  ]);
}

export interface CandidateMenuActions {
  navigate: (path: string) => void;
  openNewTab: (path: string) => void;
  copyLink: (path: string) => void;
}

/** Carrier nomzodi (ariza): driver qisqa ma'lumoti bilan -- ochish, havola, vakansiya */
export function candidateMenu(app: { driver: { id: number; full_name: string }; job: { id: number; title: string } }, a: CandidateMenuActions, t: Translate): MenuEntry[] {
  const path = `/drivers/${app.driver.id}`;
  return compactMenu([
    { key: "open-driver", label: t("openDriver", { name: app.driver.full_name }), icon: Eye, onSelect: () => a.navigate(path) },
    { key: "open-tab", label: t("openNewTab"), icon: ExternalLink, onSelect: () => a.openNewTab(path) },
    { key: "copy-link", label: t("copyLink"), icon: Link2, onSelect: () => a.copyLink(path) },
    SEPARATOR,
    { key: "open-job", label: t("driver.openJob", { title: app.job.title }), icon: Briefcase, onSelect: () => a.navigate(`/jobs/${app.job.id}`) },
  ]);
}

export interface TruckMenuActions {
  openDriver: (id: number) => void;
  copy: (text: string) => void;
  edit: () => void;
  setStatus: (status: TruckStatus) => void;
  archive: () => void;
}

/** Truck qatori: driver profili (o'z doirasida), VIN/raqamni nusxalash; canEdit ("carriers_edit") -- holat, tahrirlash, arxiv.
 * Holatlar driverga mos: driver bor -- "bo'sh" yo'q, driver yo'q -- "band" yo'q (backend ham shunday to'g'rilaydi) */
export function truckMenu(tr: TruckRow, canEdit: boolean, a: TruckMenuActions, t: Translate, tc: Translate): MenuEntry[] {
  const hasDriver = !!tr.driver_ref;
  const statuses: TruckStatus[] = hasDriver ? ["assigned", "in_shop", "inactive"] : ["available", "in_shop", "inactive"];
  return compactMenu([
    tr.driver_ref?.can_open && { key: "driver", label: t("truck.openDriver", { name: tr.driver_ref.full_name }), icon: UserRound, onSelect: () => a.openDriver(tr.driver_ref!.id) },
    tr.co_driver_ref?.can_open && { key: "co-driver", label: t("truck.openDriver", { name: tr.co_driver_ref.full_name }), icon: UserRound, onSelect: () => a.openDriver(tr.co_driver_ref!.id) },
    SEPARATOR,
    !!tr.vin && { key: "copy-vin", label: t("truck.copyVin"), icon: Copy, hint: tr.vin, onSelect: () => a.copy(tr.vin) },
    !!tr.plate_number && { key: "copy-plate", label: t("truck.copyPlate"), icon: Copy, hint: [tr.plate_number, tr.plate_state].filter(Boolean).join(" "), onSelect: () => a.copy(tr.plate_number) },
    SEPARATOR,
    canEdit && {
      key: "status",
      label: t("setStatus"),
      icon: CircleDot,
      hint: tc(`status.${tr.status}`),
      items: statuses.map((s) => ({ key: `status-${s}`, label: tc(`status.${s}`), dot: STATUS_BG[s], checked: s === tr.status, disabled: s === tr.status, onSelect: () => a.setStatus(s) })),
    },
    canEdit && { key: "edit", label: t("edit"), icon: Pencil, onSelect: a.edit },
    SEPARATOR,
    canEdit && { key: "archive", label: t("archive"), icon: Archive, danger: true, onSelect: a.archive },
  ]);
}
